import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAppContext } from '../context/AppContext';

const STATUS_COLORS = { 'В процессе': '#FF9500', 'Выполнен': '#4CAF50', 'Отменён': '#FF3B30' };
const CITIES = ['Казань', 'Зеленодольск', 'Волжск'];
const DISTRICTS = ['Мирный', 'Город', 'Зеленый дол'];

const buildAddress = (a) => [a.street, a.house ? `д. ${a.house}` : null, a.entrance ? `пд. ${a.entrance}` : null, a.floor ? `эт. ${a.floor}` : null, a.apartment ? `кв. ${a.apartment}` : null].filter(Boolean).join(', ');
const todayISO = () => new Date().toISOString().split('T')[0];
const tomorrowISO = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; };
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const formatDeliveryDate = (day) => {
  if (!day) return '—';
  const parts = day.split('-');
  if (parts.length !== 3) return day;
  const short = `${parts[2]}.${parts[1]}`;
  const date = new Date(day); date.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate()+1);
  if (date.getTime() === today.getTime()) return `Сегодня · ${short}`;
  if (date.getTime() === tomorrow.getTime()) return `Завтра · ${short}`;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

export default function ProfileScreen() {
  const { user, clearUser, isDark, toggleTheme } = useAppContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [statDay, setStatDay] = useState('today');
  const [cancelingId, setCancelingId] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addAddrVisible, setAddAddrVisible] = useState(false);
  const EMPTY_ADDR = { label: '', city: null, district: null, street: '', house: '', entrance: '', floor: '', apartment: '' };
  const [newAddr, setNewAddr] = useState(EMPTY_ADDR);
  const addrKey = `saved_addresses_${user?.id}`;

  const loadData = useCallback(async () => {
    try {
      if (user?.isAdmin) {
        setOrders((await api.orders.adminAll()) || []);
      } else {
        const raw = localStorage.getItem(addrKey);
        setSavedAddresses(raw ? JSON.parse(raw) : []);
        setOrders((await api.orders.my()) || []);
      }
    } catch {} finally { setLoading(false); }
  }, [user, addrKey]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCancel = async (orderId) => {
    if (!window.confirm('Вы уверены, что хотите отменить этот заказ?')) return;
    setCancelingId(orderId);
    try {
      await api.orders.cancel(orderId, null);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'cancelled' } : o));
    } catch { alert('Нет соединения с сервером'); }
    finally { setCancelingId(null); }
  };

  const handleAddAddress = () => {
    if (!newAddr.city) { alert('Укажите город'); return; }
    if (!newAddr.street.trim()) { alert('Укажите улицу'); return; }
    if (!newAddr.house.trim()) { alert('Укажите номер дома'); return; }
    if (newAddr.city === 'Зеленодольск' && !newAddr.district) { alert('Укажите район'); return; }
    const entry = { id: Date.now().toString(), label: newAddr.label.trim() || newAddr.city, city: newAddr.city, district: newAddr.district || null, street: newAddr.street.trim(), house: newAddr.house.trim(), entrance: newAddr.entrance.trim(), floor: newAddr.floor.trim(), apartment: newAddr.apartment.trim() };
    const updated = [...savedAddresses, entry];
    localStorage.setItem(addrKey, JSON.stringify(updated));
    setSavedAddresses(updated);
    setAddAddrVisible(false);
    setNewAddr(EMPTY_ADDR);
  };

  const handleDeleteAddress = (id) => {
    if (!window.confirm('Удалить адрес?')) return;
    const updated = savedAddresses.filter(a => a.id !== id);
    localStorage.setItem(addrKey, JSON.stringify(updated));
    setSavedAddresses(updated);
  };

  const avatarLetter = user?.name?.[0]?.toUpperCase() || '?';
  const activeOrders = orders.filter(o => !o.status || o.status === 'В процессе');
  const archivedOrders = orders.filter(o => o.status === 'Выполнен' || o.status === 'Отменён');

  const filtered = orders.filter(o => {
    if (statDay === 'today') return o.delivery_day === todayISO();
    if (statDay === 'tomorrow') return o.delivery_day === tomorrowISO();
    return true;
  });
  const totalBottles = filtered.reduce((s, o) => s + (o.quantity || 0), 0);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', fontSize: 24 }}>💧</div>;

  return (
    <div style={{ padding: '0 16px 100px' }}>
      {/* Шапка профиля */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 24, marginBottom: 24 }}>
        <div style={{ width: 80, height: 80, borderRadius: 40, background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 36, color: '#fff', fontWeight: 'bold' }}>{avatarLetter}</span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 'bold', color: 'var(--text)' }}>{user?.name || '—'}</div>
        <div style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 2 }}>{user?.email}</div>
        {user?.phone && <div style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 2 }}>{user.phone}</div>}
      </div>

      {/* Тема */}
      <div style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{isDark ? '🌙 Тёмная тема' : '☀️ Светлая тема'}</span>
        <button onClick={toggleTheme} style={{ padding: '6px 16px', borderRadius: 20, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          {isDark ? 'Светлая' : 'Тёмная'}
        </button>
      </div>

      {/* КЛИЕНТ */}
      {!user?.isAdmin && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h2 style={sectionTitle}>Мои адреса</h2>
            <button onClick={() => setAddAddrVisible(true)} style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Добавить</button>
          </div>
          {savedAddresses.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', marginBottom: 16 }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>Нет сохранённых адресов</p>
              <p style={{ fontSize: 13, color: 'var(--text-light)' }}>Добавьте адрес для быстрого заполнения заказа</p>
            </div>
          ) : savedAddresses.map(a => (
            <div key={a.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{a.label}</div>
                <div style={{ fontSize: 14, color: 'var(--text)' }}>{buildAddress(a)}</div>
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>{a.city}{a.district ? ` · ${a.district}` : ''}</div>
              </div>
              <button onClick={() => handleDeleteAddress(a.id)} style={{ background: 'none', color: '#FF3B30', fontSize: 18, fontWeight: 700, padding: 8, cursor: 'pointer' }}>✕</button>
            </div>
          ))}

          <h2 style={sectionTitle}>Активные заказы</h2>
          {activeOrders.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', marginBottom: 16 }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>Сейчас нет активных заказов</p>
              <p style={{ fontSize: 13, color: 'var(--text-light)' }}>Оформите новый заказ в каталоге</p>
            </div>
          ) : activeOrders.map(o => <OrderCard key={o.id} order={o} showCancel onCancel={handleCancel} cancelingId={cancelingId} />)}

          <div onClick={() => setHistoryExpanded(p => !p)} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>История заказов</span>
            <span style={{ color: 'var(--text-light)', fontWeight: 700 }}>{historyExpanded ? '▲' : '▼'}</span>
          </div>
          {historyExpanded && (archivedOrders.length === 0
            ? <div style={{ ...cardStyle, textAlign: 'center', marginBottom: 16 }}><p style={{ fontWeight: 600 }}>История пока пуста</p></div>
            : archivedOrders.map(o => <OrderCard key={o.id} order={o} showCancel={false} />)
          )}
        </>
      )}

      {/* АДМИН */}
      {user?.isAdmin && (
        <>
          <h2 style={sectionTitle}>Баллоны на доставку</h2>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[{ key: 'today', label: 'Сегодня' }, { key: 'tomorrow', label: 'Завтра' }, { key: 'both', label: 'Общее' }].map(({ key, label }) => (
              <button key={key} onClick={() => setStatDay(key)} style={{ flex: 1, padding: '8px 4px', borderRadius: 10, border: '1.5px solid', borderColor: statDay === key ? 'var(--primary)' : 'var(--border)', background: statDay === key ? 'var(--primary)' : 'var(--card)', color: statDay === key ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ textAlign: 'center', paddingBottom: 12 }}>
              <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--primary)' }}>{totalBottles}</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)', fontWeight: 600 }}>баллонов</div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {CITIES.map(city => {
                const b = filtered.filter(o => o.city === city).reduce((s, o) => s + (o.quantity || 0), 0);
                if (!b) return null;
                return <div key={city} style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 15 }}>{city}</span><span style={{ fontWeight: 700, color: 'var(--primary)' }}>{b}</span></div>;
              })}
            </div>
          </div>
        </>
      )}

      <button onClick={() => { if (window.confirm('Выйти из аккаунта?')) clearUser(); }} style={{ width: '100%', padding: 14, borderRadius: 14, border: '1.5px solid #FF3B30', background: 'none', color: '#FF3B30', fontSize: 16, fontWeight: 600, marginTop: 24, cursor: 'pointer' }}>
        Выйти из аккаунта
      </button>

      {/* Модальное окно добавления адреса */}
      {addAddrVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }} onClick={() => { setAddAddrVisible(false); setNewAddr(EMPTY_ADDR); }}>
          <div style={{ background: 'var(--card)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>Новый адрес</h3>
            <input placeholder="Название (Дом, Работа...)" value={newAddr.label} onChange={e => setNewAddr(p => ({ ...p, label: e.target.value }))} style={modalInputStyle} />
            <label style={modalLabelStyle}>Город</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {CITIES.map(c => <button key={c} type="button" onClick={() => setNewAddr(p => ({ ...p, city: c, district: null }))} style={{ flex: 1, padding: '8px 4px', borderRadius: 10, border: '1.5px solid', borderColor: newAddr.city === c ? 'var(--primary)' : 'var(--border)', background: newAddr.city === c ? 'var(--primary)' : 'var(--bg)', color: newAddr.city === c ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{c}</button>)}
            </div>
            {newAddr.city === 'Зеленодольск' && (
              <>
                <label style={modalLabelStyle}>Район</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  {DISTRICTS.map(d => <button key={d} type="button" onClick={() => setNewAddr(p => ({ ...p, district: d }))} style={{ flex: 1, padding: '8px 4px', borderRadius: 10, border: '1.5px solid', borderColor: newAddr.district === d ? 'var(--primary)' : 'var(--border)', background: newAddr.district === d ? 'var(--primary)' : 'var(--bg)', color: newAddr.district === d ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{d}</button>)}
                </div>
              </>
            )}
            <label style={modalLabelStyle}>Улица <span style={{ color: '#FF3B30' }}>*</span></label>
            <input placeholder="ул. Ленина" value={newAddr.street} onChange={e => setNewAddr(p => ({ ...p, street: e.target.value }))} style={modalInputStyle} />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={modalLabelStyle}>Дом *</label><input placeholder="1" value={newAddr.house} onChange={e => setNewAddr(p => ({ ...p, house: e.target.value }))} style={modalInputStyle} /></div>
              <div style={{ flex: 1 }}><label style={modalLabelStyle}>Подъезд</label><input placeholder="2" value={newAddr.entrance} onChange={e => setNewAddr(p => ({ ...p, entrance: e.target.value }))} style={modalInputStyle} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={modalLabelStyle}>Этаж</label><input placeholder="5" value={newAddr.floor} onChange={e => setNewAddr(p => ({ ...p, floor: e.target.value }))} style={modalInputStyle} /></div>
              <div style={{ flex: 1 }}><label style={modalLabelStyle}>Квартира</label><input placeholder="10" value={newAddr.apartment} onChange={e => setNewAddr(p => ({ ...p, apartment: e.target.value }))} style={modalInputStyle} /></div>
            </div>
            <button onClick={handleAddAddress} style={{ width: '100%', padding: 14, borderRadius: 12, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 16, marginTop: 16, cursor: 'pointer' }}>Сохранить</button>
            <button onClick={() => { setAddAddrVisible(false); setNewAddr(EMPTY_ADDR); }} style={{ width: '100%', padding: 10, background: 'none', color: 'var(--text-light)', fontSize: 15, fontWeight: 600, marginTop: 8, cursor: 'pointer' }}>Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, showCancel, onCancel, cancelingId }) {
  const statusColor = STATUS_COLORS[order.status] || '#FF9500';
  const statusLabel = order.status || 'В процессе';
  const orderTotal = order.total_price ?? order.price * order.quantity;
  return (
    <div style={{ ...cardStyle, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>№{order.id?.toString().slice(0, 8)}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: statusColor, background: statusColor + '20', padding: '2px 8px', borderRadius: 8 }}>{statusLabel}</span>
      </div>
      <div style={{ fontSize: 14, marginBottom: 2 }}>{order.brand} {order.volume} × {order.quantity}</div>
      <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 2 }}>Обмен: {order.exchange_count ?? 0} · День: {formatDeliveryDate(order.delivery_day)}</div>
      <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 4 }}>📍 {order.address}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: 'var(--text-light)' }}>{formatDate(order.created_at)}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{orderTotal} ₽</span>
      </div>
      {showCancel && (
        <button onClick={() => onCancel(order.id)} disabled={cancelingId === order.id} style={{ marginTop: 8, width: '100%', padding: '6px', borderRadius: 10, border: '1.5px solid #FF3B30', background: 'none', color: '#FF3B30', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: cancelingId === order.id ? 0.6 : 1 }}>
          {cancelingId === order.id ? 'Отмена...' : 'Отменить заказ'}
        </button>
      )}
    </div>
  );
}

const cardStyle = { background: 'var(--card)', borderRadius: 14, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };
const sectionTitle = { fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8, marginTop: 8 };
const modalInputStyle = { display: 'block', width: '100%', background: 'var(--bg)', borderRadius: 10, border: '1.5px solid var(--border)', padding: '10px 14px', fontSize: 15, color: 'var(--text)', outline: 'none', marginBottom: 4 };
const modalLabelStyle = { display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginTop: 8, marginBottom: 6 };
